import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EventSummaryDto } from '@rw/shared';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { LoadingState } from '../../components/states';
import { Button } from '../../components/ui';
import { eventKeys, invalidateEvents, useAuditLog, useGameSettings, useModerationEvents } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t as tr, useT } from '../../i18n';
import { AUDIT_ACTION_LABEL } from './admin-format';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { RULE_LABEL, eventTiming } from '../events/event-format';
import { Toggle } from '../profile/SettingsCard';
import { BrandsTab } from './BrandsTab';
import { CampaignsTab } from './CampaignsTab';
import { OffersTab } from './OffersTab';
import { LedgerTab } from './LedgerTab';
import { CitiesTab } from './CitiesTab';
import { StatsTab } from './StatsTab';
import { UsersTab } from './UsersTab';

type Tab = 'stats' | 'users' | 'events' | 'brands' | 'campaigns' | 'offers' | 'ledger' | 'cities' | 'audit';
const TAB_LABEL: Record<Tab, string> = { stats: 'Stats', users: 'Users', events: 'Events', brands: 'Brands', campaigns: 'Ads', offers: 'Offers', ledger: 'Ledger', cities: 'Cities', audit: 'Audit' };

/** Game owner: game stats, and moderation (events are run by their organisers; the owner can hide or feature them). */
export function AdminPage() {
  const role = useAuth((s) => s.user?.role);
  const [tab, setTab] = useState<Tab>('stats');
  const navigate = useNavigate();
  const t = useT();
  if (role !== 'SUPER_ADMIN') return <Navigate to="/" replace />;
  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        ← {t('Back')}
      </button>
      <h1 className="font-display text-4xl font-extrabold uppercase">{t('Game owner')}</h1>
      <p className="text-sm text-ink-400">{t('Stats, accounts and moderation · every action is logged.')}</p>
      <div className="mt-4 grid grid-cols-3 gap-1 rounded-2xl bg-ink-850 p-1">
        {(['stats', 'users', 'events', 'brands', 'campaigns', 'offers', 'ledger', 'cities', 'audit'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`h-10 rounded-xl text-sm font-bold uppercase ${tab === k ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
          >
            {t(TAB_LABEL[k])}
          </button>
        ))}
      </div>
      {tab === 'stats' ? <StatsTab /> : tab === 'users' ? <UsersTab /> : tab === 'events' ? <EventsModeration /> : tab === 'cities' ? <CitiesTab /> : tab === 'brands' ? <BrandsTab /> : tab === 'campaigns' ? <CampaignsTab /> : tab === 'offers' ? <OffersTab /> : tab === 'ledger' ? <LedgerTab /> : <AuditTab />}
    </Page>
  );
}

function EventsModeration() {
  const q = useModerationEvents();
  const t = useT();
  const qc = useQueryClient();
  const moderate = useMutation({
    mutationFn: (v: { id: string; hidden?: boolean; official?: boolean }) =>
      api(`/admin/events/${v.id}`, { method: 'PATCH', body: { hidden: v.hidden, official: v.official } }),
    onSuccess: () => {
      toast({ kind: 'success', title: tr('Event updated') });
      invalidateEvents(qc);
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Action failed') }),
  });
  const review = useMutation({
    mutationFn: (v: { id: string; approve: boolean }) => api(`/admin/events/${v.id}/review`, { body: { approve: v.approve } }),
    onSuccess: (_, v) => {
      toast({ kind: 'success', title: tr(v.approve ? 'Event approved' : 'Event declined') });
      invalidateEvents(qc);
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Action failed') }),
  });
  if (q.isPending) return <LoadingState />;
  if (!q.data?.length)
    return (
      <>
        <EventCreationSwitch />
        <p className="mt-4 text-sm text-ink-500">{t('No events yet.')}</p>
      </>
    );
  const pending = q.data.filter((e) => e.approval === 'PENDING' && e.status !== 'CANCELLED');
  return (
    <>
      <EventCreationSwitch />
      {pending.length ? (
        <>
          <div className="mb-2 mt-5 text-xs font-bold uppercase tracking-widest text-gold">{t('Waiting for approval · {n}', { n: pending.length })}</div>
          <ul className="space-y-2" data-testid="pending-events">
            {pending.map((e) => (
              <li key={e.id} className="rounded-xl border border-gold/30 bg-gold/10 p-3 text-sm">
                <Link to={`/events/${e.id}`} className="block">
                  <div className="font-semibold">{e.name}</div>
                  <div className="text-xs text-ink-400">
                    {t(RULE_LABEL[e.rule])} · {eventTiming(e.status, e.startsAt, e.endsAt)} · {t('by {name}', { name: e.organizer.displayName })}
                    {e.visibility === 'PRIVATE' ? ` · ${t('private')}` : ''}
                  </div>
                </Link>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => review.mutate({ id: e.id, approve: true })} disabled={review.isPending || e.status === 'ENDED'} data-testid="approve-event">
                    {t('Approve')}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-enemy" onClick={() => review.mutate({ id: e.id, approve: false })} disabled={review.isPending}>
                    {t('Decline')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <ul className="mt-4 space-y-2" data-testid="moderation-events">
        {q.data.filter((e) => !pending.includes(e)).map((e: EventSummaryDto & { hidden?: boolean }) => (
          <li key={e.id} className="rounded-xl bg-ink-850 p-3 text-sm">
            <Link to={`/events/${e.id}`} className="block">
              <div className="font-semibold">
                {e.official ? '⭐ ' : ''}
                {e.name}
              </div>
              <div className="text-xs text-ink-500">
                {t(RULE_LABEL[e.rule])} · {eventTiming(e.status, e.startsAt, e.endsAt)} · {t('{n} runners', { n: e.participants })} ·{' '}
                {t('by {name}', { name: e.organizer.displayName })}
                {e.visibility === 'PRIVATE' ? ` · ${t('private')}` : ''}
                {e.approval === 'PENDING' ? ` · ${t('withdrawn request')}` : e.approval === 'REJECTED' ? ` · ${t('declined')}` : ''}
              </div>
            </Link>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => moderate.mutate({ id: e.id, official: !e.official })}>
                {t(e.official ? 'Unmark official' : 'Mark official')}
              </Button>
              <Button size="sm" variant="ghost" className="text-enemy" onClick={() => moderate.mutate({ id: e.id, hidden: true })}>
                {t('Hide')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => moderate.mutate({ id: e.id, hidden: false })}>
                {t('Unhide')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Who may create events: every runner, or only the game owner. */
function EventCreationSwitch() {
  const q = useGameSettings();
  const t = useT();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (eventCreationOpen: boolean) => api('/admin/settings', { method: 'PATCH', body: { eventCreationOpen } }),
    onSuccess: (_, open) => {
      toast({ kind: 'success', title: tr(open ? 'Every runner can create events' : 'Only you can create events now') });
      void qc.invalidateQueries({ queryKey: ['admin'] });
      void qc.invalidateQueries({ queryKey: eventKeys.list });
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Action failed') }),
  });
  return (
    <div className="mt-4 rounded-xl bg-ink-850 p-3">
      <Toggle
        id="event-creation-open"
        checked={save.isPending ? !!save.variables : (q.data?.eventCreationOpen ?? true)}
        disabled={q.isPending || save.isPending}
        onChange={(v) => save.mutate(v)}
        label={t('Runners can create events')}
        description={t('On: each runner creates a few events freely (below); more wait for your approval. Off: only you can create events. Existing events are not affected.')}
      />
      {q.data ? <FreeEventsField key={q.data.freeEventsPerRunner} value={q.data.freeEventsPerRunner} /> : null}
    </div>
  );
}

/** How many events a runner can create in the life of their account before each needs approval. */
function FreeEventsField({ value }: { value: number }) {
  const [n, setN] = useState(String(value));
  const t = useT();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (freeEventsPerRunner: number) => api('/admin/settings', { method: 'PATCH', body: { freeEventsPerRunner } }),
    onSuccess: (_, v) => {
      toast({ kind: 'success', title: tr('Runners now get {n} free events', { n: v }) });
      void qc.invalidateQueries({ queryKey: ['admin'] });
      void qc.invalidateQueries({ queryKey: eventKeys.list });
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Action failed') }),
  });
  const parsed = Number(n);
  const valid = n.trim() !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= 100;
  return (
    <div className="mt-3 border-t border-ink-700 pt-3">
      <label htmlFor="free-events" className="text-sm font-semibold text-ink-100">
        {t('Free events per runner')}
      </label>
      <p className="text-xs text-ink-400">{t('Over the life of the account (cancelled ones don’t count). Past this, each new event waits for your approval.')}</p>
      <div className="mt-2 flex gap-2">
        <input
          id="free-events"
          type="number"
          inputMode="numeric"
          min={0}
          max={100}
          value={n}
          onChange={(e) => setN(e.target.value)}
          className="h-10 w-24 rounded-xl border border-ink-700 bg-ink-900 px-3 text-ink-100"
          data-testid="free-events-input"
        />
        <Button size="sm" variant="secondary" onClick={() => save.mutate(parsed)} disabled={!valid || parsed === value} loading={save.isPending}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
}

function AuditTab() {
  const q = useAuditLog();
  const t = useT();
  if (q.isPending) return <LoadingState />;
  return (
    <ul className="mt-4 space-y-2" data-testid="audit-log">
      {q.data?.length === 0 ? <p className="text-sm text-ink-500">{t('No moderation actions yet.')}</p> : null}
      {q.data?.map((l) => (
        <li key={l.id} className="rounded-xl bg-ink-850 p-3 text-sm">
          <div className="flex justify-between gap-2">
            <span className="font-semibold">{l.actor.displayName}</span>
            <span className="text-xs text-ink-500">{formatDateTime(l.createdAt)}</span>
          </div>
          <div className="text-ink-300">
            {AUDIT_ACTION_LABEL[l.action] ? t(AUDIT_ACTION_LABEL[l.action]!) : l.action}
            {typeof l.details.name === 'string' ? ` · ${l.details.name}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}
